// @flow
import { useCallback, useEffect } from "react";

type Props = {
  outerRef: any,
  disableEnter?: boolean,
  handleClose: () => void,
  ignoreSelectors?: string[]
};

const useClickoutside = ({
  outerRef,
  handleClose,
  disableEnter,
  ignoreSelectors = []
}: Props) => {
  const getEl = (): HTMLElement | null => {
    if (
      outerRef &&
      typeof outerRef === "object" &&
      outerRef !== null &&
      "current" in outerRef
    ) {
      return outerRef.current instanceof HTMLElement ? outerRef.current : null;
    }
    return outerRef instanceof HTMLElement ? outerRef : null;
  };

  const isInsideIgnored = (node: any): boolean => {
    if (!(node instanceof Node)) return false;
    for (let i = 0; i < ignoreSelectors.length; i++) {
      const sel = ignoreSelectors[i];
      let match = null;
      if (typeof (node: any).closest === "function") {
        match = (node: any).closest(sel);
      }
      if (match) return true;
    }
    return false;
  };

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const el = getEl();
      if (!el) return;

      const target = (event.target: any);
      if (!(target instanceof Node)) return;

      if (isInsideIgnored(target)) return;

      if (!el.contains(target)) return;

      const childDialog: any = el.querySelector(
        '[aria-modal="true"], [role="dialog"]'
      );
      if (childDialog) return;

      if (event.key === "Enter" && !disableEnter) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    },
    [handleClose, disableEnter, ignoreSelectors]
  );

  const onDocumentClick = useCallback(
    (event: MouseEvent) => {
      const el = getEl();
      if (!el) return;

      const target = (event.target: any);
      if (!(target instanceof Node)) return;

      if (isInsideIgnored(target)) return;

      const childDialog: any = el.querySelector(
        '[aria-modal="true"], [role="dialog"]'
      );
      if (childDialog && childDialog.contains(target)) return;

      if (!el.contains(target)) {
        handleClose();
      }
    },
    [handleClose, ignoreSelectors]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("click", onDocumentClick, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [onKeyDown, onDocumentClick]);
};

export default useClickoutside;
